import { useState, useEffect, useRef, useCallback } from 'react'
import Editor from '@monaco-editor/react'
import { Button } from './ui/button'
import { loadDatabase, saveDatabase, executeQuery as localExecuteQuery, loadHistoryFromIndexedDB, clearHistoryInIndexedDB } from '../database/db'
import { Card } from './ui/card'
import { ScrollArea } from './ui/scroll-area'
import { Separator } from './ui/separator'
import { toast } from 'sonner'
import {
  Database,
  Play,
  RotateCcw,
  History,
  LogOut,
  User,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Table as TableIcon,
  RefreshCw as Sync, // Using RefreshCw as a suitable icon for Sync
  Download,
  Upload,
  Sparkles, // Changed icon to Sparkles for AI Assistant
  Loader2 // Added Loader2 for loading indicator
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from './ui/alert-dialog'
import { Input } from './ui/input'
import axios from 'axios' // Import axios for backend API calls

// Suppress ResizeObserver loop errors in development
if (process.env.NODE_ENV === 'development') {
  const originalError = console.error
  console.error = (...args) => {
    if (
      args[0] &&
      typeof args[0] === 'string' &&
      args[0].includes(
        'ResizeObserver loop completed with undelivered notifications'
      )
    ) {
      return
    }
    originalError.apply(console, args)
  }
}

const WorkspacePage = ({ user, onLogout }) => {
  const editorRef = useRef(null)
  const dbRef = useRef(null) // Ref to hold the sql.js database instance
  const executeQueryRef = useRef(null) // Ref to hold the latest executeQuery function
  const fileInputRef = useRef(null) // Ref for the hidden file input

  const [query, setQuery] = useState('SELECT * FROM students;')
  const [queryResult, setQueryResult] = useState(null)
  const [history, setHistory] = useState([]) // History will be managed locally or removed
  const [schema, setSchema] = useState({})
  const [executing, setExecuting] = useState(false)
  const [dbLoaded, setDbLoaded] = useState(false) // New state to track DB loading
  const [showResetDialog, setShowResetDialog] = useState(false)
  const [showClearHistoryDialog, setShowClearHistoryDialog] = useState(false)
  const [showAiPromptDialog, setShowAiPromptDialog] = useState(false) // State for AI prompt dialog
  const [aiPrompt, setAiPrompt] = useState('') // State for AI prompt input
  const [generatingSql, setGeneratingSql] = useState(false) // State for SQL generation loading

  // Initialize database and load history on component mount
  useEffect(() => {
    const initDb = async () => {
      console.log("Initializing database..."); // Log start of DB initialization
      try {
        const database = await loadDatabase()
        dbRef.current = database
        setDbLoaded(true)
        loadSchema()
        loadHistory()
        console.log("Database initialized and loaded:", dbRef.current); // Log successful DB load
      } catch (error) {
        console.error("Failed to initialize database:", error); // Log any errors during DB init
        toast.error("Failed to load database. Please refresh the page.");
      }
    }
    initDb()
  }, [])

  const handleEditorDidMount = useCallback(editor => {
    editorRef.current = editor
    editor.addCommand(
      window.monaco.KeyMod.CtrlCmd | window.monaco.KeyCode.Enter,
      () => {
        executeQueryRef.current()
      },
      null
    )
  }, [])

  const loadHistory = useCallback(async () => {
    try {
      const historyData = await loadHistoryFromIndexedDB()
      setHistory(historyData.reverse()) // Display most recent first
    } catch (error) {
      console.error('Failed to load history from IndexedDB', error)
      toast.error('Failed to load query history')
    }
  }, [])

  const loadSchema = useCallback(async () => {
    if (!dbRef.current) return

    try {
      const result = dbRef.current.exec(
        "SELECT name FROM sqlite_master WHERE type='table';"
      )
      const tableNames =
        result[0]?.values.map(row => row[0]).filter(name => name !== 'sqlite_sequence') || [] // Filter out sqlite_sequence

      const schemaData = {}
      for (const tableName of tableNames) {
        const tableInfo = dbRef.current.exec(`PRAGMA table_info(${tableName});`)
        schemaData[tableName] = tableInfo[0]?.values.map(row => ({
          cid: row[0],
          name: row[1],
          type: row[2],
          notnull: row[3],
          dflt_value: row[4],
          pk: row[5]
        })) || []
      }
      setSchema(schemaData)
    } catch (error) {
      console.error('Failed to load schema from local DB', error)
      toast.error('Failed to load schema')
    }
  }, [])

  const executeQuery = useCallback(async () => {
    if (!query.trim()) {
      toast.error('Please enter a query')
      return
    }
    if (!dbRef.current) {
      toast.error('Database not loaded yet.')
      return
    }

    setExecuting(true)
    try {
      const { success, result, error, executionTime, affectedRows } = await localExecuteQuery(query) // Await the async function
      console.log("Result from localExecuteQuery:", { success, result, error, executionTime, affectedRows }); // Log the raw result

      if (success) {
        setQueryResult({
          success: true,
          columns: result[0]?.columns || [],
          rows: result[0]?.values || [],
          affected_rows: affectedRows,
          execution_time_ms: parseFloat(executionTime)
        })
        toast.success(`Query executed in ${executionTime}ms`)

        // Check if the query might have created or dropped a table and refresh schema
        const upperCaseQuery = query.trim().toUpperCase();
        if (upperCaseQuery.startsWith('CREATE TABLE') || upperCaseQuery.startsWith('DROP TABLE')) {
          loadSchema()
        }
      } else {
        setQueryResult({ success: false, error })
        toast.error(error)
      }

      loadHistory() // Refresh history after execution
    } catch (error) {
      toast.error('Query execution failed: ' + error.message)
    } finally {
      setExecuting(false)
    }
  }, [query, loadSchema, loadHistory])

  // Update the ref whenever executeQuery changes
  useEffect(() => {
    executeQueryRef.current = executeQuery
  }, [executeQuery])

  const resetDatabase = async () => {
    try {
      // Re-initialize the database to an empty state
      const newDb = await loadDatabase(true) // Pass true to force a fresh DB
      dbRef.current = newDb
      await saveDatabase() // Persist the empty DB
      toast.success('Database reset successfully')
      setQueryResult(null)
      loadSchema()
      setShowResetDialog(false)
    } catch (error) {
      console.error('Failed to reset local database', error)
      toast.error('Failed to reset database')
    }
  }

  const handleDownloadDb = async () => {
    if (!dbRef.current) {
      toast.error('Database not loaded yet.')
      return
    }
    try {
      await saveDatabase() // Ensure the latest state is saved to IndexedDB
      const binaryArray = dbRef.current.export()
      const blob = new Blob([binaryArray], { type: 'application/x-sqlite3' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'datapilot.db'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('Database downloaded successfully!')
    } catch (error) {
      console.error('Failed to download database', error)
      toast.error('Failed to download database')
    }
  }

  const handleUploadDb = async event => {
    const file = event.target.files[0]
    if (!file) {
      return
    }

    const reader = new FileReader()
    reader.onload = async e => {
      try {
        const U8Array = new Uint8Array(e.target.result)
        const newDb = await loadDatabase(false, U8Array) // Pass U8Array to loadDatabase
        dbRef.current = newDb
        await saveDatabase() // Persist the uploaded DB
        toast.success('Database uploaded successfully!')
        setQueryResult(null)
        loadSchema()
      } catch (error) {
        console.error('Failed to upload database', error)
        toast.error('Failed to upload database. Make sure it is a valid SQLite .db file.')
      }
    }
    reader.readAsArrayBuffer(file)
  }

  // Sync functionality is removed as DB is local
  // const syncDatabase = async () => { ... }

  const clearHistory = async () => {
    try {
      await clearHistoryInIndexedDB()
      setHistory([])
      toast.success('Query history cleared')
      setShowClearHistoryDialog(false)
    } catch (error) {
      console.error('Failed to clear history from IndexedDB', error)
      toast.error('Failed to clear query history')
    }
  }

  const loadQueryFromHistory = historyQuery => {
    setQuery(historyQuery)
    toast.info('Query loaded from history')
  }

  const handleGenerateSql = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter a prompt for the AI Assistant.')
      return
    }

    setGeneratingSql(true)
    const startTime = Date.now(); // Record start time
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found. Please log in.");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v2/generate-sql/`,
        { prompt: aiPrompt, schema: schema },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const generatedSql = response.data.sql_query;
      const endTime = Date.now(); // Record end time
      const duration = endTime - startTime; // Calculate duration

      setQuery(prevQuery => (prevQuery.trim() ? prevQuery + '\n' + generatedSql : generatedSql))
      toast.success(`SQL query generated successfully in ${duration}ms!`)

    } catch (error) {
      console.error('Error generating SQL:', error)
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      toast.error('Failed to generate SQL query: ' + errorMessage)
    } finally {
      setGeneratingSql(false)
      setAiPrompt('') // Clear prompt input
      setShowAiPromptDialog(false) // Close dialog
    }
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-50 to-blue-50'>
      {/* Header */}
      <header
        className='border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50'
        data-testid='workspace-header'
      >
        <div className='container mx-auto px-4 py-3 flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div className='h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-md'>
              <Database className='h-5 w-5 text-white' />
            </div>
            <div>
              <h1 className='text-xl font-bold text-slate-900'>DataPilot v2</h1>
              <p className='text-xs text-slate-500'>SQL Learning Workspace</p>
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={handleDownloadDb}
              data-testid='download-db-button'
            >
              <Download className='h-4 w-4 mr-2' />
              Download DB
            </Button>

            <input
              type='file'
              ref={fileInputRef}
              onChange={handleUploadDb}
              style={{ display: 'none' }}
              accept='.db,.sqlite,.sqlite3'
            />
            <Button
              variant='outline'
              size='sm'
              onClick={() => fileInputRef.current.click()}
              data-testid='upload-db-button'
            >
              <Upload className='h-4 w-4 mr-2' />
              Upload DB
            </Button>

            <Button
              variant='outline'
              size='sm'
              onClick={() => setShowResetDialog(true)}
              data-testid='reset-db-button'
            >
              <RotateCcw className='h-4 w-4 mr-2' />
              Reset DB
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='sm'
                  data-testid='user-menu-button'
                >
                  <User className='h-4 w-4 mr-2' />
                  {user?.full_name}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <div className='px-2 py-1.5 text-sm'>
                  <div className='font-medium'>{user?.full_name}</div>
                  {user?.email && <div className='text-xs text-slate-500'>{user.email}</div>}
                  {user?.phone_number && <div className='text-xs text-slate-500'>{user.phone_number}</div>}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onLogout}
                  data-testid='logout-button'
                >
                  <LogOut className='h-4 w-4 mr-2' />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className='container mx-auto p-4'>
        <div
          className='grid lg:grid-cols-[1fr_350px] gap-4'
          data-testid='workspace-grid'
        >
          {/* Main content */}
          <div className='space-y-4'>
            {/* Database Schema */}
            <Card
              className='p-4 bg-white/80 backdrop-blur-sm border-slate-200'
              data-testid='schema-panel'
            >
              <div className='flex items-center gap-2 mb-3'>
                <TableIcon className='h-5 w-5 text-blue-600' />
                <h2 className='font-semibold text-slate-900'>
                  Database Schema
                </h2>
              </div>
              <div className='flex gap-4 flex-wrap'>
                {Object.keys(schema).map(tableName => (
                  <div
                    key={tableName}
                    className='px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm'
                    data-testid={`schema-table-${tableName}`}
                  >
                    <span className='font-medium text-blue-900'>
                      {tableName}
                    </span>
                    <span className='text-blue-600 ml-2'>
                      ({schema[tableName].length} columns)
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            {/* SQL Editor */}
            <Card
              className='overflow-hidden bg-white border-slate-200'
              data-testid='editor-panel'
            >
              <div className='p-4 border-b border-slate-200 flex items-center justify-between'>
                <h2 className='font-semibold text-slate-900'>SQL Editor</h2>
                <Button
                  onClick={executeQuery}
                  disabled={executing || !dbLoaded}
                  data-testid='execute-query-button'
                >
                  <Play className='h-4 w-4 mr-2' />
                  {executing ? 'Executing...' : (dbLoaded ? 'Execute Query' : 'Loading DB...')}
                </Button>
              </div>
              <div className='monaco-editor-wrapper relative'> {/* Added relative for absolute positioning of the button */}
                <Button
                  size='icon'
                  className='rounded-full w-10 h-10 absolute top-3 right-3 z-10 bg-gradient-to-br from-purple-500 to-indigo-600 text-white shadow-lg hover:from-purple-600 hover:to-indigo-700 hover:shadow-2xl hover:shadow-purple-500/70 hover:text-white transition-all duration-200'
                  data-testid='ai-assistant-button'
                  onClick={() => setShowAiPromptDialog(true)}
                >
                  <Sparkles className='h-6 w-6' />
                </Button>
                <Editor
                  height='250px'
                  defaultLanguage='sql'
                  value={query}
                  onChange={value => setQuery(value || '')}
                  onMount={handleEditorDidMount}
                  theme='vs'
                  options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                    lineHeight: 20,
                    padding: { top: 12, bottom: 12 }
                  }}
                />
              </div>
            </Card>

            {/* Query Results */}
            {queryResult && (
              <Card
                className='p-4 bg-white border-slate-200'
                data-testid='results-panel'
              >
                <div className='flex items-center justify-between mb-3'>
                  <h2 className='font-semibold text-slate-900'>
                    Query Results
                  </h2>
                  {queryResult.success ? (
                    <div className='flex items-center gap-2 text-sm text-green-600'>
                      <CheckCircle className='h-4 w-4' />
                      <span>Success</span>
                    </div>
                  ) : (
                    <div className='flex items-center gap-2 text-sm text-red-600'>
                      <XCircle className='h-4 w-4' />
                      <span>Error</span>
                    </div>
                  )}
                </div>

                {queryResult.success ? (
                  queryResult.columns ? (
                    <div className='overflow-auto max-h-96'>
                      <table className='w-full text-sm border-collapse'>
                        <thead>
                          <tr className='bg-slate-100'>
                            {queryResult.columns.map((col, idx) => (
                              <th
                                key={idx}
                                className='border border-slate-300 px-4 py-2 text-left font-semibold text-slate-700'
                                data-testid={`result-header-${col}`}
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {queryResult.rows.map((row, rowIdx) => (
                            <tr key={rowIdx} className='hover:bg-slate-50'>
                              {row.map((cell, cellIdx) => (
                                <td
                                  key={cellIdx}
                                  className='border border-slate-300 px-4 py-2 text-slate-700'
                                  data-testid={`result-cell-${rowIdx}-${cellIdx}`}
                                >
                                  {cell !== null ? String(cell) : 'NULL'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className='mt-2 text-sm text-slate-500'>
                        {queryResult.rows.length} row(s) returned
                      </div>
                    </div>
                  ) : (
                    <div className='text-sm text-slate-600'>
                      Query executed successfully. {queryResult.affected_rows}{' '}
                      row(s) affected.
                    </div>
                  )
                ) : (
                  <div className='p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700'>
                    {queryResult.error}
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Query History Sidebar */}
          <Card
            className='bg-white border-slate-200 h-fit lg:sticky lg:top-20'
            data-testid='history-panel'
          >
            <div className='p-4 border-b border-slate-200 flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <History className='h-5 w-5 text-slate-700' />
                <h2 className='font-semibold text-slate-900'>Query History</h2>
              </div>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setShowClearHistoryDialog(true)}
                data-testid='clear-history-button'
              >
                <Trash2 className='h-4 w-4' />
              </Button>
            </div>

            <ScrollArea className='h-[600px]'>
              <div className='p-4 space-y-2'>
                {history.length === 0 ? (
                  <p className='text-sm text-slate-500 text-center py-8'>
                    No query history yet
                  </p>
                ) : (
                  history.map(item => (
                    <button
                      key={item.id}
                      onClick={() => loadQueryFromHistory(item.query)}
                      className='w-full text-left p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors'
                      data-testid={`history-item-${item.id}`}
                    >
                      <div className='flex items-start justify-between gap-2 mb-2'>
                        <div className='flex items-center gap-1.5'>
                          {item.success ? (
                            <CheckCircle className='h-3.5 w-3.5 text-green-600 flex-shrink-0' />
                          ) : (
                            <XCircle className='h-3.5 w-3.5 text-red-600 flex-shrink-0' />
                          )}
                          <span className='text-xs text-slate-500 flex items-center gap-1'>
                            <Clock className='h-3 w-3' />
                            {new Date(item.executed_at).toLocaleTimeString()}
                          </span>
                        </div>
                        {item.execution_time_ms !== null && (
                          <span className='text-xs text-slate-400'>
                            {item.execution_time_ms === 0
                              ? '0ms'
                              : `${item.execution_time_ms.toFixed(0)}ms`}
                          </span>
                        )}
                      </div>
                      <code className='text-xs text-slate-700 line-clamp-2 block'>
                        {item.query}
                      </code>
                      {item.error_message && (
                        <p className='text-xs text-red-600 mt-1 line-clamp-1'>
                          {item.error_message}
                        </p>
                      )}
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </Card>
        </div>
      </div>

      {/* Reset Database Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent data-testid='reset-dialog'>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Database?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all your current data and restore the database to
              its initial state with sample data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid='reset-cancel-button'>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={resetDatabase}
              data-testid='reset-confirm-button'
            >
              Reset Database
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear History Dialog */}
      <AlertDialog
        open={showClearHistoryDialog}
        onOpenChange={setShowClearHistoryDialog}
      >
        <AlertDialogContent data-testid='clear-history-dialog'>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Query History?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all your query history. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid='clear-history-cancel-button'>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={clearHistory}
              data-testid='clear-history-confirm-button'
            >
              Clear History
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI Prompt Dialog */}
      <AlertDialog open={showAiPromptDialog} onOpenChange={setShowAiPromptDialog}>
        <AlertDialogContent data-testid='ai-prompt-dialog'>
          <AlertDialogHeader>
            <AlertDialogTitle>AI SQL Assistant</AlertDialogTitle>
            <AlertDialogDescription>
              Enter a natural language prompt to generate an SQL query.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='grid gap-4 py-4'>
            <Input
              id='ai-prompt'
              placeholder='e.g., "Show all records in the students table"'
              className='col-span-3'
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter' && !generatingSql) {
                  handleGenerateSql();
                }
              }}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setAiPrompt('');
                setShowAiPromptDialog(false);
              }}
              data-testid='ai-prompt-cancel-button'
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleGenerateSql}
              disabled={generatingSql}
              data-testid='ai-prompt-generate-button'
            >
              {generatingSql ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Generating...
                </>
              ) : (
                'Generate SQL'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>

  )
}

export default WorkspacePage;
